import { Button, FormGroup, InputGroup } from '@blueprintjs/core'
import { Select } from '@blueprintjs/select'
import { WorkspaceUser } from 'enablex/sdk'
import { Dialog, lang } from 'enablex/shared'
import { AuthRole, AuthStrategyConfig, CreatedUser } from 'common/typings'
import React, { FC, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import api from '~/app/api'
import { AppState } from '~/app/rootReducer'

import AuthStrategyDropdown from './AuthStrategyDropdown'
import { fetchAvailableUsers } from './reducer'
import RoleDropdown from './RoleDropdown'

interface Department {
  id: string
  name: string
}

type Props = {
  isOpen?: boolean
  toggleOpen?: () => void
  onUserAdded?: () => void
  onUserCreated?: (newUser: CreatedUser) => void
  forcedRoleId?: string
} & ConnectedProps<typeof connector>

interface UserOption {
  label: string
  value: WorkspaceUser
  __isNew__?: boolean
}

export const CreateUserModal: FC<Props> = props => {
  const [role, setRole] = useState<AuthRole>()
  const [strategy, setStrategy] = useState<AuthStrategyConfig>()
  const [selectedUser, setSelectedUser] = useState<string>('')
  //const [skill, setSkill] = useState('')
  const [isAudioVideoSupported, setAudioVideoSupported] = useState(true)
  const [firstname, setFirstname] = useState<string>('')
  const [supervisors, setSupervisors] = useState<WorkspaceUser[]>([])
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('')
  // Department state
  const [departments, setDepartments] = useState<Department[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<string>('')

  const handleAudioVideoSupportFlag = e => {
    setAudioVideoSupported(e.target.checked)
  }

  const regexValidation = (str) => {
    const regex = /^[a-zA-Z][a-zA-Z0-9@._\/]*$/
    if (regex.test(str)) {
      return true
    }
    return false
  }

  useEffect(() => {
    props.fetchAvailableUsers()
    setSelectedUser('')
    // Fetch supervisors when modal opens
    if (props.isOpen) {
      void fetchSupervisors()
      void fetchDepartments()
    }
  }, [props.isOpen])

  const fetchSupervisors = async () => {
    try {
      const { data } = await api.getSecured().get('/admin/workspace/collaborators/supervisors')
      setSupervisors(data.payload)
    } catch (err) {
      console.error('Failed to fetch supervisors:', err)
    }
  }

  // Fetch departments for the current workspace
  const fetchDepartments = async () => {
    try {
      const { data } = await api.getSecured().get('/admin/workspace/departments')
      setDepartments(data.payload.departments || [])
    } catch (err) {
      console.error('Failed to fetch departments:', err)
    }
  }

  const isCreating = selectedUser && regexValidation(selectedUser)
  const isValid = selectedUser && role && (!isCreating || (isCreating && strategy))

  const createUser = async () => {
    if (!selectedUser || !role) {
      return
    }

    if (isCreating) {
      if (!strategy) {
        return
      }

      const { data } = await api.getSecured().post('/admin/workspace/collaborators', {
        email: selectedUser,
        strategy: strategy.strategyId,
        role: role.id,
        ...(firstname.length > 0 ? { firstname } : {}),
        isAudioVideoSupported,
        supervisor: selectedSupervisor, // Add supervisor to the request
        ...(selectedDepartment ? { department_id: selectedDepartment } : {})
      })
      setFirstname('')
      props.onUserCreated && props.onUserCreated(data.payload)
    } else {
      const email = selectedUser
      await api.getSecured().post('/admin/workspace/collaborators/workspace/add', {
        email,
        strategy: 'default',
        role: role.id,
        supervisor: selectedSupervisor, // Add supervisor to the request
        ...(selectedDepartment ? { department_id: selectedDepartment } : {})
      })
      props.onUserAdded && props.onUserAdded()
    }
  }

  const st = {
    strategyType: 'basic',
    strategyId: 'default',
    loginUrl: '/login/basic/default',
    registerUrl: '/register/basic/default'
  }

  return (
    <Dialog.Wrapper
      title={lang.tr('admin.workspace.users.collaborators.add')}
      icon="add"
      isOpen={props.isOpen}
      onClose={props.toggleOpen}
      onSubmit={createUser}
    >
      <Dialog.Body>
        <FormGroup
          label={lang.tr('Username')}
          labelFor="input-email"
          helperText={<><b>Note:</b><span>The username must be alphanumeric with few special characters (@ - _ .)<br /><span>e.g., agent1@workspace.com</span></span></>}
        >
          <InputGroup
            id="input-email"
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            autoFocus={true}
            placeholder="Enter username"
          />
        </FormGroup>
        <FormGroup label={lang.tr('admin.fullName')} labelFor="input-firstname">
            <InputGroup
              id="input-firstname"
              value={firstname}
              onChange={e => setFirstname(e.target.value)}
              tabIndex={1}
              autoFocus={false}
              placeholder="Enter name"
          />
        </FormGroup>
        {/* {isCreating && (
          <FormGroup label={lang.tr('admin.workspace.users.collaborators.authStrategy')} labelFor="select-strategy">
            <AuthStrategyDropdown onChange={strategy => setStrategy(strategy)} />
          </FormGroup>
        )} */}

        <FormGroup label={lang.tr('admin.workspace.users.collaborators.chooseRole')} labelFor="select-role">
          <RoleDropdown onChange={
            role => {
              setRole(role)
              setStrategy(st)
            }
           }/>
        </FormGroup>

        {/* Department dropdown */}
        <FormGroup label="Department" labelFor="select-department">
          <Select
            items={[{ id: '', name: 'Select department...' }, ...departments]}
            itemRenderer={(department, { handleClick }) => (
              <div
                key={department.id}
                onClick={handleClick}
                style={{ padding: '5px 10px', cursor: 'pointer', fontStyle: department.id === '' ? 'italic' : 'normal', color: department.id === '' ? '#888' : undefined }}
              >
                {department.name}
              </div>
            )}
            onItemSelect={department => setSelectedDepartment(department.id)}
            filterable={false}
            popoverProps={{ minimal: true }}
          >
            <Button
              text={
                selectedDepartment
                  ? (departments.find(d => d.id === selectedDepartment)?.name || 'Select department...')
                  : 'Select department...'
              }
              rightIcon="caret-down"
              fill
            />
          </Select>
        </FormGroup>

        {role && role.id === 'agent' && (
        <>
        <FormGroup label={lang.tr('Choose a Supervisor')} labelFor="select-supervisor">
          <Select
            items={supervisors}
            itemRenderer={(supervisor, { handleClick }) => (
              <div key={supervisor.email} onClick={handleClick} style={{ padding: '5px 10px', cursor: 'pointer' }}>
                {supervisor.email}
              </div>
            )}
            onItemSelect={supervisor => setSelectedSupervisor(supervisor.email)}
            filterable={false}
            popoverProps={{ minimal: true }}
          >
            <Button
              text={selectedSupervisor || 'Select a supervisor...'}
              rightIcon="caret-down"
              fill
            />
          </Select>
        </FormGroup>
        <FormGroup label={lang.tr('Advanced Agent')}>
            <input
              type="checkbox"
              id="audio-video-support"
              checked={isAudioVideoSupported}
              onChange={handleAudioVideoSupportFlag}
            />
            <label htmlFor="audio-video-support">Audio/Video equipped</label>
        </FormGroup>
        </>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        {
          <Button
            id="btn-submit-create-user"
            type="submit"
            text={lang.tr('admin.workspace.users.collaborators.createAccount')}
            disabled={!isValid}
          />
        }
      </Dialog.Footer>
    </Dialog.Wrapper>
  )
}

const mapStateToProps = (state: AppState) => ({
  availableUsers: state.collaborators.availableUsers
})

const connector = connect(mapStateToProps, { fetchAvailableUsers })

export default connector(CreateUserModal)
